import * as THREE from 'three';
import { scene } from './scene';
/* ============================================================
   預警層 —— 四色講「牠怎麼傷你」，形狀講「你該怎麼閃」
   ============================================================

   規格 `05-ENEMY.md` §9 把這一層叫做「辨識系統缺的第三條腿」，
   也是 GDD 支柱一（每一種會致死的東西都要先講一次）在敵人這一側的實作。

   | 形狀 | 意思 | 用在 |
   |---|---|---|
   | 地面圈（填色由內往外充滿） | 走出去 | 紫・範圍 |
   | 地面線（由暗到亮） | 閃到旁邊 | 橙・直射、紅・撲擊 |
   | 擴散環 | 已經發生了（回饋，不是預警） | 命中瞬間 |
   | 細邊光環（脈動、不收縮、不填色） | **不會傷你**，是加成範圍 | 天藍・支援 |

   ## 為什麼是四顆 InstancedMesh 而不是規格寫的三顆

   第四顆是天藍的支援光環。規格原本規定「天藍刻意沒有任何框」，
   使用者 2026-08-03 選擇破例，理由是不畫的話玩家不知道要先打誰。
   破例的代價（「有框＝會傷你」這條學習規則被破壞）用**畫法**補回來：

     預警：實心填色 ＋ 由內往外充滿 ＋ 觸發瞬間消失   → 「這裡快要痛了」
     支援：只有細邊 ＋ 半徑固定 ＋ 緩慢脈動           → 「這裡有事發生，但不是痛」

   兩者在 20px 下也分得出來（一個在變、一個不變）。

   ## 為什麼不用透明度做淡出

   `opacity` 是材質層級的，per-instance 改不了。而 2026-08-02 已經踩過
   「加法混色的預告環打在亮地板上幾乎全白」——所以預警一律**用尺寸與
   `instanceColor` 的亮度表達進度，不淡出**；只有命中擴散環用加法混色
   （它只存在 0.3 秒，讀作閃光而不是圖形）。

   ⚠️ `instanceColor` 的老地雷（規格 §6.5）：要生效必須開 `vertexColors`，
   一開了幾何又沒有 `color` 屬性，WebGL 餵 (0,0,0) → 整個變全黑。
   所以下面每個幾何都要過 `withColor()`。 */

function withColor(g) {
  var n = g.attributes.position.count, a = new Float32Array(n * 3);
  for (var i = 0; i < n * 3; i++) a[i] = 1;
  g.setAttribute('color', new THREE.BufferAttribute(a, 3));
  return g;
}

/** 實體上限。**軟上限 `SOFT` 才是視覺上真正重要的那個**：

    第一次實看抓到的問題 —— 四隻 GEISSEL 同時起手，四個半徑 5.5 的填色圈
    疊在玩家身上就糊成一片白（`.claude/notes/shots/enemy-b1/e1-geissel.png`）。
    而後期紫色佔權重 19%、場上 300 隻，那會是五十幾個圈。

    規格 §9 的預警層是在**圖鑑檢視台**裡設計的（一次看一隻），
    它沒有遇過同屏 300 隻。所以這裡加兩道限制：
      1. 同時最多 `SOFT` 筆（先到先得，滿了新的就不畫）
      2. 圖形整體壓薄壓暗（見 `ensure()` 的 opacity）

    「少數幾個看得懂」勝過「全部都畫但看不懂」—— 預警的目的是讓玩家
    知道往哪閃，畫滿整個地板等於沒畫。 */
var CAP = 96;
var SOFT = 16;
var _o = new THREE.Object3D(), _c = new THREE.Color();

function flat(geo, mat) {
  var im = new THREE.InstancedMesh(geo, mat, CAP);
  im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(CAP * 3).fill(1), 3);
  im.instanceColor.setUsage(THREE.DynamicDrawUsage);
  im.frustumCulled = false; im.count = 0; im.renderOrder = -2;
  return im;
}

var MESH: any = null;
function ensure() {
  if (MESH) return MESH;
  var base = { transparent: true, depthWrite: false, vertexColors: true, side: THREE.DoubleSide };
  MESH = {
    fill: flat(withColor(new THREE.CircleGeometry(1, 24)),
      new THREE.MeshBasicMaterial(Object.assign({ opacity: 0.16 }, base))),
    ring: flat(withColor(new THREE.RingGeometry(0.945, 1, 28)),
      new THREE.MeshBasicMaterial(Object.assign({ opacity: 0.62 }, base))),
    line: flat(withColor(new THREE.PlaneGeometry(1, 1)),
      new THREE.MeshBasicMaterial(Object.assign({ opacity: 0.30 }, base))),
    burst: flat(withColor(new THREE.RingGeometry(0.78, 1, 20)),
      new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, vertexColors: true, opacity: 0.9, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }))
  };
  /* 躺平一律寫在**實例矩陣**裡，不寫在本體上 —— 兩邊都寫會轉兩次 −90°，
     圖形立起來變成牆、而且世界 y 會拿到 z 的值。同型的錯在 `enemy-field.ts`
     的接地陰影躺了一整輪都沒被發現（那段程式從沒被執行過）。 */
  for (var k in MESH) scene.add(MESH[k]);
  return MESH;
}

/* 一筆預警。固定長度的環形緩衝 —— 滿了就覆蓋最舊的那一筆，
   而不是不畫新的：**新的預警比舊的重要**（舊的那一下已經快結束了）。 */
type Warn = { k: number, x: number, z: number, a: number, r: number, len: number, t: number, dur: number, hex: number, e: any };
var LIST: Warn[] = [];
function slot(): Warn | null {
  if (LIST.length >= SOFT) return null;      // 軟上限，見上面
  var w: any = {}; LIST.push(w); return w;
}

/** 紫・範圍：地面圈。填色由內往外充滿，滿了就是打下來的瞬間。 */
export function warnCircle(x, z, r, dur, hex) {
  var w = slot(); if (!w) return;
  w.k = 0; w.x = x; w.z = z; w.r = r; w.t = 0; w.dur = dur; w.hex = hex; w.e = null; w.a = 0; w.len = 0;
}
/** 橙・直射／紅・撲擊：地面線。由暗到亮，亮到底就是出手。 */
export function warnLine(x, z, nx, nz, len, dur, hex) {
  var w = slot(); if (!w) return;
  w.k = 1; w.x = x; w.z = z; w.a = Math.atan2(nx, nz); w.len = len; w.t = 0; w.dur = dur; w.hex = hex; w.e = null; w.r = 0;
}
/** 天藍・支援：細邊光環。跟著施放者移動、半徑固定、只脈動不收縮。 */
export function warnAura(e, r, dur, hex) {
  var w = slot(); if (!w) return;
  w.k = 2; w.e = e; w.r = r; w.t = 0; w.dur = dur; w.hex = hex; w.x = e.pos.x; w.z = e.pos.z; w.a = 0; w.len = 0;
}
/** 命中回饋：擴散環。**這不是預警**（事情已經發生了），所以用加法混色。 */
export function warnBurst(x, z, r, hex) {
  var w = slot(); if (!w) return;
  w.k = 3; w.x = x; w.z = z; w.r = r; w.t = 0; w.dur = 0.3; w.hex = hex; w.e = null; w.a = 0; w.len = 0;
}

/** 全部清掉（開新的一場）。留著的話上一場的預警會浮在新場地上。 */
export function clearWarnFx() { LIST.length = 0; }

/** 推進生命週期。跑在**模擬時間**上（由 `sim/fx.ts` 的 `updateFx` 呼叫）——
    預警的長度是 `tTel`，而 `tTel` 是模擬秒，兩者必須用同一個時鐘，
    否則頓幀時預警會比攻擊早結束（玩家會覺得「明明閃掉了還是被打到」）。 */
export function updateWarnFx(dt) {
  for (var i = LIST.length - 1; i >= 0; i--) {
    var w = LIST[i];
    w.t += dt;
    if (w.t >= w.dur || (w.e && !w.e.alive)) LIST.splice(i, 1);
  }
}

/** 寫進 InstancedMesh。每個畫面幀一次，在 `syncEnemyField` 旁邊。 */
export function syncWarnFx() {
  var M = ensure(), n = { fill: 0, ring: 0, line: 0, burst: 0 };
  for (var i = 0; i < LIST.length; i++) {
    var w = LIST[i], p = Math.min(1, w.t / w.dur);
    if (w.k === 0) {
      /* 外框固定＝範圍有多大；填色由內往外＝還有多久。
         兩件事分開講，玩家才能同時讀到「多大」與「多快」。 */
      put(M.ring, n.ring++, w.x, w.z, w.r, w.r, 0, w.hex, 0.55 + 0.45 * p);
      put(M.fill, n.fill++, w.x, w.z, w.r * p, w.r * p, 0, w.hex, 1);
    } else if (w.k === 1) {
      /* 線的原點在敵人身上，所以往前推半個長度才會是「從牠到你」那一段。 */
      var hx = w.x + Math.sin(w.a) * w.len * 0.5, hz = w.z + Math.cos(w.a) * w.len * 0.5;
      put(M.line, n.line++, hx, hz, 0.55, w.len, w.a, w.hex, 0.35 + 0.65 * p);
    } else if (w.k === 2) {
      /* 半徑固定、只脈動 —— 這是「不會傷你」的畫法，見檔頭。 */
      var k = 1 + 0.035 * Math.sin(w.t * 6.2);
      put(M.ring, n.ring++, w.e.pos.x, w.e.pos.z, w.r * k, w.r * k, 0, w.hex, 0.5 + 0.3 * Math.sin(w.t * 6.2) * 0.5);
    } else {
      var s = w.r * (0.35 + 1.15 * p);
      put(M.burst, n.burst++, w.x, w.z, s, s, 0, w.hex, 1 - p);
    }
  }
  for (var k2 in n) {
    var im = M[k2];
    im.count = n[k2];
    im.instanceMatrix.needsUpdate = true;
    im.instanceColor.needsUpdate = true;
  }
}

/** 地板上的一片。Euler 是 XYZ 序（R = Rx·Ry·Rz），所以先繞自己的 Z 轉 `a`
    再整片躺平：長軸（local Y）落在世界的 −(sin a, cos a)。
    矩形對 180° 旋轉對稱，所以方向相反不影響外觀。 */
function put(im, idx, x, z, sx, sy, a, hex, bright) {
  if (idx >= CAP) return;
  _o.position.set(x, 0.05, z);
  _o.rotation.set(-Math.PI / 2, 0, a);
  _o.scale.set(sx, sy, 1);
  _o.updateMatrix();
  im.setMatrixAt(idx, _o.matrix);
  _c.setHex(hex).multiplyScalar(bright);
  im.setColorAt(idx, _c);
}
